import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import MenuBar from "@/components/MenuBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ImageUpload } from "@/components/ui/ImageUpload";
import { toast } from "sonner";
import { ArrowLeft, Copy, Mail } from "lucide-react";
import { AMAService } from "@/services/amaService";
import { AMA, AMAStatus } from "@/types/ama";
import { baseEmailConfig } from "@/utils/emailConfig";

const AMAForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  const { data: ama } = useQuery({
    queryKey: ['ama', id],
    queryFn: () => AMAService.getAMAById(id!),
    enabled: isEdit,
  });

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<Partial<AMA>>({
    defaultValues: ama || {
      status: 'planned',
      is_featured: false,
    },
  });

  const [status, setStatus] = useState<AMAStatus>(ama?.status || 'planned');
  const [isFeatured, setIsFeatured] = useState(ama?.is_featured || false);

  const createMutation = useMutation({
    mutationFn: (data: Partial<AMA>) => AMAService.createAMA(data),
    onSuccess: (data) => {
      toast.success(t('ama.form.createSuccess'));
      queryClient.invalidateQueries({ queryKey: ['amas'] });
      queryClient.invalidateQueries({ queryKey: ['current-ama'] });
      navigate(`/editorial/ama/${data.id}`);
    },
    onError: () => {
      toast.error(t('ama.form.createError'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<AMA>) => AMAService.updateAMA(id!, data),
    onSuccess: () => {
      toast.success(t('ama.form.updateSuccess'));
      queryClient.invalidateQueries({ queryKey: ['amas'] });
      queryClient.invalidateQueries({ queryKey: ['ama', id] });
      queryClient.invalidateQueries({ queryKey: ['current-ama'] });
    },
    onError: () => {
      toast.error(t('ama.form.updateError'));
    },
  });

  const onSubmit = (data: Partial<AMA>) => {
    const payload = {
      ...data,
      status,
      is_featured: isFeatured,
    };

    if (isEdit) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const expertLink = ama ? `${baseEmailConfig.baseUrl}/ama/answer/${ama.expert_token}` : '';

  const copyLink = () => {
    navigator.clipboard.writeText(expertLink);
    toast.success(t('ama.form.linkCopied'));
  };

  const sendInvitation = () => {
    const subject = encodeURIComponent(`Invitasjon til AMA: ${watch('topic')}`);
    const body = encodeURIComponent(
      `Hei ${watch('expert_name')},\n\n` +
      `Du er invitert til å delta i en AMA (Ask Me Anything) på Briefr.\n\n` +
      `Tema: ${watch('topic')}\n` +
      `Dato: ${new Date(watch('scheduled_at') || '').toLocaleString('no-NO')}\n\n` +
      `Bruk denne lenken for å svare på spørsmål:\n${expertLink}\n\n` +
      `Lenken er unik og personlig for deg. Vennligst ikke del den med andre.\n\n` +
      `Mvh,\nBriefr-redaksjonen`
    );
    
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <MenuBar />
      
      <div className="container mx-auto p-6 max-w-3xl">
      <Button
        variant="ghost"
        onClick={() => navigate('/editorial/ama')}
        className="mb-4"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        {t('common.back')}
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>
            {isEdit ? t('ama.form.editTitle') : t('ama.form.createTitle')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="topic">{t('ama.form.topic')} *</Label>
              <Input
                id="topic"
                {...register('topic', { required: true })}
                placeholder={t('ama.form.topicPlaceholder')}
              />
              {errors.topic && (
                <p className="text-sm text-destructive">{t('common.required')}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Kontekst</Label>
              <Textarea
                id="description"
                {...register('description')}
                placeholder="Beskriv konteksten for AMAen og hvem intervjuobjektet er..."
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="expert_name">{t('ama.form.expertName')} *</Label>
                <Input
                  id="expert_name"
                  {...register('expert_name', { required: true })}
                  placeholder={t('ama.form.expertNamePlaceholder')}
                />
                {errors.expert_name && (
                  <p className="text-sm text-destructive">{t('common.required')}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="expert_title">{t('ama.form.expertTitle')}</Label>
                <Input
                  id="expert_title"
                  {...register('expert_title')}
                  placeholder={t('ama.form.expertTitlePlaceholder')}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="expert_company">{t('ama.form.expertCompany')}</Label>
              <Input
                id="expert_company"
                {...register('expert_company')}
                placeholder={t('ama.form.expertCompanyPlaceholder')}
              />
            </div>

            <ImageUpload
              value={watch('expert_photo_url') || ''}
              onChange={(url) => setValue('expert_photo_url', url)}
              label={t('ama.form.expertPhoto')}
              bucket="profile-images"
              folder="ama-experts"
            />

            <div className="space-y-2">
              <Label htmlFor="scheduled_at">{t('ama.form.scheduledAt')} *</Label>
              <Input
                id="scheduled_at"
                type="datetime-local"
                {...register('scheduled_at', { required: true })}
              />
              {errors.scheduled_at && (
                <p className="text-sm text-destructive">{t('common.required')}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">{t('ama.form.status')}</Label>
              <Select value={status} onValueChange={(value) => setStatus(value as AMAStatus)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="planned">{t('ama.status.planned')}</SelectItem>
                  <SelectItem value="live">{t('ama.status.live')}</SelectItem>
                  <SelectItem value="archived">{t('ama.status.archived')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="is_featured"
                checked={isFeatured}
                onCheckedChange={setIsFeatured}
              />
              <Label htmlFor="is_featured">{t('ama.form.featured')}</Label>
            </div>

            {isEdit && expertLink && (
              <Card className="bg-muted">
                <CardContent className="pt-6 space-y-4">
                  <div>
                    <Label>{t('ama.form.expertLink')}</Label>
                    <div className="flex gap-2 mt-2">
                      <Input value={expertLink} readOnly className="flex-1" />
                      <Button type="button" variant="outline" size="icon" onClick={copyLink}>
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button type="button" variant="outline" onClick={sendInvitation}>
                        <Mail className="mr-2 h-4 w-4" />
                        {t('ama.form.sendInvitation')}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="flex gap-2">
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                {isEdit ? t('common.save') : t('ama.form.create')}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/editorial/ama')}
              >
                {t('common.cancel')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default AMAForm;
